import React, { useMemo } from 'react';
import type { Components } from 'react-markdown';

import type { GetAssistantSessionsBySessionIdMessages200MessagesItemCitations as Citations } from '@/api/generated/lucioAPI.schemas';
import { SmoothMarkdown } from '@/components/SmoothMarkdown';

import { CitationButton } from './CitationTooltip';

interface CitationAwareMarkdownProps {
  /**
   * Enables assistant-chat-specific text handling: stripping the streaming
   * `<citations>` trailer, hiding markers mid-stream, and the legacy `(N)`
   * back-compat conversion. Leave false (the default) for content that is
   * already server-processed (automation run output) or raw (user messages) —
   * those transforms would truncate the text or fabricate false citations.
   */
  assistantChat?: boolean;
  citations?: Citations | null | undefined;
  content: string;
  /** While true, citation markers are hidden (they only get sequenced at end of stream). */
  isGenerating?: boolean;
  onViewMail?: ((messageId: string) => void) | undefined;
}

/**
 * Renders markdown with inline `[[cite:N]]` markers turned into clickable
 * citation badges. Shared by the assistant chat message and the automation run
 * detail view so both surfaces render found-email citations identically.
 */
export function CitationAwareMarkdown({
  assistantChat = false,
  citations,
  content,
  isGenerating = false,
  onViewMail,
}: CitationAwareMarkdownProps) {
  const processedContent = useMemo(() => {
    let processed = content;

    if (assistantChat) {
      // Strip the citation trailer (mid-stream too — partial blocks would
      // otherwise flicker as raw JSON before the stream completes). Scoped to
      // assistant chat: a user message containing a literal "<citations>" must
      // not be truncated, and automation output is already trailer-stripped
      // server-side.
      processed = processed
        .replaceAll(/<citations>[\s\S]*?<\/citations>/gi, '')
        .replace(/<citations>[\s\S]*$/i, '')
        .trimEnd();

      if (isGenerating) {
        // Hide every citation marker while the stream is in flight. The model
        // emits pre-sequenced (and occasionally plain-paren) numbers that only
        // get remapped to sequential ones at end of stream; rendering them
        // mid-stream shows random-looking numbers.
        processed = processed.replaceAll(/\s?\[\[cite:[^\]]*\]?\]?/g, '');
        processed = processed.replace(/\s?\[\[(?:c(?:i(?:t(?:e:?)?)?)?)?$/, '');
        processed = processed.replaceAll(/\s?\(\d+\)/g, '');
        return processed;
      }
    }

    if (!citations) {
      return processed;
    }

    // Replace [[cite:1]] with an internal marker we can intercept downstream.
    processed = processed.replaceAll(/\[\[cite:(\d+)\]\]/g, (match, number) => {
      if (citations?.[number]) {
        return `[CITE:${number}]`;
      }
      return match;
    });

    if (assistantChat) {
      // Back-compat: chat messages persisted before the [[cite:N]] migration
      // still carry plain (N) markers. Convert only when N maps to a known
      // citation, so legitimate parenthesised numbers in prose (e.g. "(2024)")
      // are kept. Scoped to chat: automation output never used the legacy
      // format, so applying it there would only fabricate false citations.
      processed = processed.replaceAll(/\((\d+)\)/g, (match, number) => {
        if (citations?.[number]) {
          return `[CITE:${number}]`;
        }
        return match;
      });
    }

    return processed;
  }, [assistantChat, content, citations, isGenerating]);

  const CitationInline = ({ citationNumber }: { citationNumber: string }) => {
    if (!citations?.[citationNumber]) {
      return <span>({citationNumber})</span>;
    }
    return (
      <CitationButton
        citation={citations[citationNumber]}
        citationNumber={citationNumber}
        onViewMail={onViewMail}
      />
    );
  };

  const renderText = (text: string): Array<string | React.ReactNode> => {
    if (!citations) {
      return [text];
    }

    // Split by citation markers [CITE:1], [CITE:2], etc.
    const parts = text.split(/(\[CITE:\d+\])/);

    return parts.map((part, index) => {
      const citationRegex = /\[CITE:(\d+)\]/;
      const citationMatch = citationRegex.exec(part);
      if (citationMatch) {
        const citationNumber = citationMatch[1];
        return (
          <CitationInline
            citationNumber={citationNumber!}
            key={`cite-${citationNumber}-${index}`}
          />
        );
      }
      return part;
    });
  };

  // Recursively process children to handle citations at any nesting level
  const processChildren = (children: React.ReactNode): React.ReactNode => {
    if (typeof children === 'string') {
      return renderText(children);
    }

    if (Array.isArray(children)) {
      return children.map((child, index) => {
        if (typeof child === 'string') {
          return <React.Fragment key={index}>{renderText(child)}</React.Fragment>;
        }
        if (React.isValidElement(child)) {
          return React.cloneElement(
            child,
            { key: index } as never,
            processChildren((child.props as { children?: React.ReactNode }).children),
          );
        }
        return child;
      });
    }

    if (React.isValidElement(children)) {
      return React.cloneElement(
        children,
        {} as never,
        processChildren((children.props as { children?: React.ReactNode }).children),
      );
    }

    return children;
  };

  const components: Components = {
    li: ({ children, ...props }) => {
      return <li {...props}>{processChildren(children)}</li>;
    },
    ol: ({ children, ...props }) => (
      <ol className="mb-2 space-y-1" {...props}>
        {processChildren(children)}
      </ol>
    ),
    p: ({ children, ...props }) => {
      return (
        <p className="mb-2 whitespace-pre-wrap last:mb-0" {...props}>
          {processChildren(children)}
        </p>
      );
    },
    strong: ({ children, ...props }) => {
      return (
        <strong className="text-primary font-semibold" {...props}>
          {processChildren(children)}
        </strong>
      );
    },
    ul: ({ children, ...props }) => (
      <ul className="mb-2 space-y-1" {...props}>
        {processChildren(children)}
      </ul>
    ),
  };

  return (
    <SmoothMarkdown
      components={components}
      content={content}
      isGenerating={isGenerating}
      processedContent={processedContent}
    />
  );
}
